/**
 * Meeting preview for statement savings.
 *
 * The formulas match StatementAnalysisCalculator: a percent is percent
 * points ("2.50" means 2.50%), a missing per-item or monthly fee is $0,
 * quick mode prices the card volumes, and detailed mode prices gross
 * volume. Interchange plus adds the statement's interchange dollars.
 *
 * Rates come from the price saved on the analysis, or from an active
 * pricing template. Nothing here fills in a rate that was not saved.
 */

export const CATEGORY_SUM_WARNING = 'Fee categories do not add up to the statement total fees.'

export const FEE_BUCKET_WARNING = 'Detailed fee totals do not match the quick-mode total fees.'

export const FEE_CHECK_MESSAGE = 'The fees need checking before this rate or a savings comparison can be used.'

export interface TemplateRates {
  name: string
  pricing_option: string
  is_default?: boolean
  is_active?: boolean
  bankcard_discount_rate?: number | string | null
  bankcard_per_item?: number | string | null
  discover_rate?: number | string | null
  discover_per_item?: number | string | null
  amex_rate?: number | string | null
  amex_per_item?: number | string | null
  flat_rate_bankcard?: number | string | null
  flat_rate_discover?: number | string | null
  flat_rate_amex?: number | string | null
  pin_debit_volume_rate?: number | string | null
  pin_debit_item_rate?: number | string | null
  maintenance_fee?: number | string | null
  batch_fee?: number | string | null
  auth_fee?: number | string | null
}

export interface StatementSnapshot {
  entry_mode?: string | null
  pricing_model?: string | null
  proposed_rates?: Record<string, string | number | null> | null
  gross_volume?: string | number | null
  transaction_count?: string | number | null
  total_fees?: string | number | null
  discount_fees?: string | number | null
  per_item_fees?: string | number | null
  monthly_fees?: string | number | null
  other_fees?: string | number | null
  fixed_monthly_fees?: string | number | null
  visa_volume?: string | number | null
  mastercard_volume?: string | number | null
  discover_volume?: string | number | null
  amex_volume?: string | number | null
  pin_debit_volume?: string | number | null
  visa_count?: string | number | null
  mastercard_count?: string | number | null
  discover_count?: string | number | null
  amex_count?: string | number | null
  pin_debit_count?: string | number | null
  interchange_cost_override?: string | number | null
}

export interface SavingsSliders {
  volume: number
  averageTicket: number
  pinDebitShare: number
}

export interface PricedOffer {
  cost: number
  savings: number | null
  summary: string
  source: string
}

export interface SavingsPreview {
  sales: number | null
  fees: number | null
  comparisonFees: number | null
  effectiveRate: number | null
  interchangePlus: PricedOffer | null
  flat: PricedOffer | null
  interchangePlusNote: string | null
  flatNote: string | null
  contextNote: string | null
  templateFootnote: string | null
}

interface BrandSlice {
  volume: number
  count: number
  percent: number | null
  perItem: number | null
  label: string
}

interface Scenario {
  gross: number
  brandVolumeSum: number
  count: number
  useGrossForPercent: boolean
  fees: number | null
  interchange: number | null
  brands: Array<{ key: string; label: string; volume: number; count: number }>
}

const BRANDS = [
  { key: 'visa', label: 'Visa', volume: 'visa_volume', count: 'visa_count' },
  { key: 'mastercard', label: 'Mastercard', volume: 'mastercard_volume', count: 'mastercard_count' },
  { key: 'discover', label: 'Discover', volume: 'discover_volume', count: 'discover_count' },
  { key: 'amex', label: 'Amex', volume: 'amex_volume', count: 'amex_count' },
  { key: 'pin_debit', label: 'PIN debit', volume: 'pin_debit_volume', count: 'pin_debit_count' },
] as const

export function money(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export function numberOrNull(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function categoriesMismatch(
  categories: Array<{ amount?: string | number | null }>,
  totalFees: string | number | null | undefined,
): boolean {
  if (categories.length === 0 || totalFees === null || totalFees === undefined || totalFees === '') return false
  const total = numberOrNull(totalFees)
  if (total === null) return false
  const sum = categories.reduce((carry, category) => carry + (numberOrNull(category.amount) ?? 0), 0)
  return Math.abs(Math.round(sum * 100) - Math.round(total * 100)) > 1
}

/**
 * The fees currently on the form win over the warning saved with the
 * reading. A saved warning stays only while those fees are still blank.
 */
export function categoryTotalWarning(
  categories: Array<{ amount?: string | number | null }>,
  totalFees: string | number | null | undefined,
  savedWarning: string | null | undefined,
): string | null {
  if (categories.length === 0 || totalFees === null || totalFees === undefined || totalFees === '') {
    return savedWarning || null
  }
  if (!categoriesMismatch(categories, totalFees)) return null
  return savedWarning || CATEGORY_SUM_WARNING
}

/**
 * The headline rate and the savings comparison stay hidden when the
 * fee total is missing, far below the cost of the printed card rates,
 * or the fee totals do not agree. The rep can still edit the figures.
 */
export function feesNeedChecking(input: {
  fees: number | null
  sales: number | null
  printedCost: number | null
  categoriesDisagree: boolean
  bucketsDisagree: boolean
}): boolean {
  if (input.categoriesDisagree || input.bucketsDisagree) return true
  if (input.fees === null && input.sales !== null && input.sales > 0) return true
  if (input.fees !== null && input.printedCost !== null && input.printedCost > 0 && input.fees * 2 < input.printedCost) {
    return true
  }
  return false
}

export function openingSliders(statement: StatementSnapshot): SavingsSliders | null {
  const gross = numberOrNull(statement.gross_volume)
  if (gross === null || gross <= 0) return null
  const count = baseCount(statement)
  const pin = numberOrNull(statement.pin_debit_volume) ?? 0
  return {
    volume: gross,
    averageTicket: count > 0 ? gross / count : 0,
    pinDebitShare: (pin / gross) * 100,
  }
}

export function previewSavings(
  statement: StatementSnapshot,
  sliders: SavingsSliders | null,
  templates: TemplateRates[],
  interchange: { hasResult: boolean; amount: string | number | null | undefined },
): SavingsPreview {
  const scenario = buildScenario(statement, sliders, interchange)
  const sales = numberOrNull(statement.gross_volume)
  const fees = baseFees(statement)
  const effectiveRate = sales !== null && sales > 0 && fees !== null ? (fees / sales) * 100 : null
  const priced = priceBoth(statement, scenario, templates)
  return {
    sales,
    fees,
    comparisonFees: scenario.fees,
    effectiveRate,
    ...priced,
  }
}

function priceBoth(
  statement: StatementSnapshot,
  scenario: Scenario,
  templates: TemplateRates[],
): Pick<SavingsPreview, 'interchangePlus' | 'flat' | 'interchangePlusNote' | 'flatNote' | 'contextNote' | 'templateFootnote'> {
  const model = statement.pricing_model ?? ''
  const contextNote = model === 'tiered'
    ? 'This analysis is saved as tiered pricing. These two prices come from pricing templates.'
    : model === 'cash_discount'
      ? 'This analysis is saved as cash discount. These two prices come from pricing templates.'
      : null
  const savedFlat = model === 'flat' ? savedRates(statement, 'flat_rate_percent') : null
  const savedMarkup = model === 'interchange_plus' ? savedRates(statement, 'markup_percent') : null
  const flatTemplate = savedFlat ? null : defaultTemplate(templates, 'B')
  const plusTemplate = savedMarkup ? null : defaultTemplate(templates, 'A')
  const flat = savedFlat
    ? offerFromSingle(scenario, savedFlat, null, 'Saved on this analysis', flatSummary(savedFlat))
    : offerFromTemplate(scenario, flatTemplate, 'B')
  const plus = savedMarkup
    ? offerFromSingle(
      scenario,
      savedMarkup,
      scenario.interchange,
      'Saved on this analysis',
      plusSummary(savedMarkup),
      scenario.interchange === null ? 'Interchange plus needs the interchange dollars from the statement.' : null,
    )
    : offerFromTemplate(scenario, plusTemplate, 'A')

  return {
    interchangePlus: plus.offer,
    flat: flat.offer,
    interchangePlusNote: plus.note,
    flatNote: flat.note,
    contextNote,
    templateFootnote: templateFootnote(savedFlat ? null : flatTemplate, savedMarkup ? null : plusTemplate),
  }
}

function savedRates(
  statement: StatementSnapshot,
  percentKey: 'flat_rate_percent' | 'markup_percent',
): { percent: number; perItem: number; monthly: number } | null {
  const rates = statement.proposed_rates ?? {}
  const percent = numberOrNull(rates[percentKey])
  if (percent === null) return null
  return {
    percent,
    perItem: numberOrNull(rates.per_item_fee) ?? 0,
    monthly: numberOrNull(rates.monthly_fee) ?? 0,
  }
}

function offerFromSingle(
  scenario: Scenario,
  rates: { percent: number; perItem: number; monthly: number },
  interchange: number | null,
  source: string,
  summary: string,
  blocked: string | null = null,
): { offer: PricedOffer | null; note: string | null } {
  if (blocked) return { offer: null, note: blocked }
  const base = scenario.useGrossForPercent ? scenario.gross : scenario.brandVolumeSum
  const cost = money(percentOf(base, rates.percent) + scenario.count * rates.perItem + rates.monthly + (interchange ?? 0))
  return { offer: toOffer(scenario, cost, summary, source), note: null }
}

function offerFromTemplate(
  scenario: Scenario,
  template: TemplateRates | null,
  option: 'A' | 'B',
): { offer: PricedOffer | null; note: string | null } {
  const label = option === 'A' ? 'interchange-plus' : 'flat'
  if (!template) {
    return {
      offer: null,
      note: `No ${label} price is set. Add one under Edit figures, or choose a pricing template.`,
    }
  }
  if (option === 'A' && scenario.interchange === null) {
    return { offer: null, note: 'Interchange plus needs the interchange dollars from the statement.' }
  }
  const slices = option === 'A' ? plusSlices(scenario, template) : flatSlices(scenario, template)
  if (typeof slices === 'string') return { offer: null, note: slices }
  let cost = numberOrNull(template.maintenance_fee) ?? 0
  for (const slice of slices) {
    if (slice.volume <= 0 && slice.count <= 0) continue
    cost += percentOf(slice.volume, slice.percent ?? 0)
    cost += slice.count * (slice.perItem ?? 0)
  }
  if (option === 'A') cost += scenario.interchange ?? 0
  return {
    offer: toOffer(scenario, money(cost), templateSummary(slices, template), `Pricing template: ${template.name}`),
    note: null,
  }
}

function toOffer(scenario: Scenario, cost: number, summary: string, source: string): PricedOffer {
  return {
    cost,
    savings: scenario.fees === null ? null : money(scenario.fees - cost),
    summary,
    source,
  }
}

function plusSlices(scenario: Scenario, template: TemplateRates): BrandSlice[] | string {
  return slicesFor(scenario, {
    visa: [template.bankcard_discount_rate, template.bankcard_per_item],
    mastercard: [template.bankcard_discount_rate, template.bankcard_per_item],
    discover: [template.discover_rate, template.discover_per_item],
    amex: [template.amex_rate, template.amex_per_item],
    pin_debit: [template.pin_debit_volume_rate, template.pin_debit_item_rate],
  })
}

function flatSlices(scenario: Scenario, template: TemplateRates): BrandSlice[] | string {
  return slicesFor(scenario, {
    visa: [template.flat_rate_bankcard, template.bankcard_per_item],
    mastercard: [template.flat_rate_bankcard, template.bankcard_per_item],
    discover: [template.flat_rate_discover, template.discover_per_item],
    amex: [template.flat_rate_amex, template.amex_per_item],
    pin_debit: [template.pin_debit_volume_rate, template.pin_debit_item_rate],
  })
}

function slicesFor(
  scenario: Scenario,
  rates: Record<string, [number | string | null | undefined, number | string | null | undefined]>,
): BrandSlice[] | string {
  const slices: BrandSlice[] = []
  for (const brand of scenario.brands) {
    const [percentRaw, perItemRaw] = rates[brand.key] ?? [null, null]
    const percent = numberOrNull(percentRaw)
    const perItem = numberOrNull(perItemRaw)
    if (brand.volume > 0 && percent === null) {
      return `The pricing template is missing a rate for ${brand.label}.`
    }
    slices.push({
      volume: brand.volume,
      count: brand.count,
      percent,
      perItem,
      label: brand.label,
    })
  }
  return slices
}

function templateSummary(slices: BrandSlice[], template: TemplateRates): string {
  const used = slices.filter((slice) => slice.volume > 0 && slice.percent !== null)
  const percents = new Set(used.map((slice) => slice.percent))
  const monthly = numberOrNull(template.maintenance_fee) ?? 0
  const perItemValues = used.map((slice) => slice.perItem ?? 0)
  const samePerItem = perItemValues.every((value) => value === perItemValues[0])
  const monthlyText = monthly > 0 ? `, ${dollars(monthly)} a month` : ''
  if (used.length > 0 && percents.size === 1 && samePerItem) {
    const perItem = perItemValues[0] ?? 0
    const perItemText = perItem > 0 ? `, ${dollars(perItem)} a sale` : ''
    return `${formatPercent(used[0]?.percent ?? 0)} of sales${perItemText}${monthlyText}`
  }
  const grouped = new Map<string, string[]>()
  for (const slice of used) {
    const key = formatPercent(slice.percent ?? 0)
    grouped.set(key, [...(grouped.get(key) ?? []), slice.label])
  }
  const parts = [...grouped.entries()].map(([percent, labels]) => `${labels.join(' and ')} ${percent}`)
  return `${parts.join(', ')}${monthlyText}` || `Pricing template rates${monthlyText}`
}

function flatSummary(rates: { percent: number; perItem: number; monthly: number }): string {
  return `${formatPercent(rates.percent)} of sales${feeTail(rates.perItem, rates.monthly)}`
}

function plusSummary(rates: { percent: number; perItem: number; monthly: number }): string {
  return `Statement interchange, plus ${formatPercent(rates.percent)} of sales${feeTail(rates.perItem, rates.monthly)}`
}

function feeTail(perItem: number, monthly: number): string {
  const parts = []
  if (perItem > 0) parts.push(`${dollars(perItem)} a sale`)
  if (monthly > 0) parts.push(`${dollars(monthly)} a month`)
  return parts.length > 0 ? `, ${parts.join(', ')}` : ''
}

function templateFootnote(flat: TemplateRates | null, plus: TemplateRates | null): string | null {
  const templates = [plus, flat].filter((template): template is TemplateRates => template !== null)
  const skipped = templates.some((template) => {
    const batch = numberOrNull(template.batch_fee) ?? 0
    const auth = numberOrNull(template.auth_fee) ?? 0
    return batch > 0 || auth > 0
  })
  if (!skipped) return null
  return 'A template batch fee or auth fee is not included. The statement does not price those the way this comparison does.'
}

function defaultTemplate(templates: TemplateRates[], option: 'A' | 'B'): TemplateRates | null {
  const matches = templates.filter((template) => template.is_active !== false && template.pricing_option === option)
  return matches.find((template) => template.is_default) ?? matches[0] ?? null
}

function buildScenario(
  statement: StatementSnapshot,
  sliders: SavingsSliders | null,
  interchange: { hasResult: boolean; amount: string | number | null | undefined },
): Scenario {
  const opening = openingSliders(statement)
  const custom = sliders !== null && opening !== null && (
    Math.abs(sliders.volume - opening.volume) > 0.001
    || Math.abs(sliders.averageTicket - opening.averageTicket) > 0.001
    || Math.abs(sliders.pinDebitShare - opening.pinDebitShare) > 0.001
  )
  const stored = BRANDS.map((brand) => ({
    key: brand.key,
    label: brand.label,
    volume: numberOrNull(statement[brand.volume]) ?? 0,
    count: numberOrNull(statement[brand.count]) ?? 0,
  }))
  const gross = numberOrNull(statement.gross_volume) ?? 0
  const detailed = (statement.entry_mode ?? 'detailed') === 'detailed'
  const interchangeAmount = interchange.hasResult
    ? numberOrNull(interchange.amount)
    : numberOrNull(statement.interchange_cost_override)

  if (!custom || opening === null || sliders === null || gross <= 0) {
    return {
      gross,
      brandVolumeSum: stored.reduce((sum, brand) => sum + brand.volume, 0),
      count: baseCount(statement),
      useGrossForPercent: detailed,
      fees: baseFees(statement),
      interchange: interchangeAmount,
      brands: stored,
    }
  }

  const volume = Math.max(0, sliders.volume)
  const ratio = volume / gross
  const pin = money(volume * clamp(sliders.pinDebitShare, 0, 100) / 100)
  const creditTarget = money(volume - pin)
  const creditStored = stored.filter((brand) => brand.key !== 'pin_debit')
  const creditWeights = creditStored.map((brand) => brand.volume)
  const creditVolumes = splitToTotal(creditTarget, creditWeights)
  const brands = stored.map((brand) => {
    if (brand.key === 'pin_debit') return { ...brand, volume: pin, count: 0 }
    const index = creditStored.findIndex((credit) => credit.key === brand.key)
    return { ...brand, volume: creditVolumes[index] ?? 0, count: 0 }
  })
  const count = sliders.averageTicket > 0 ? Math.max(1, Math.round(volume / sliders.averageTicket)) : 0
  const volumeSum = brands.reduce((sum, brand) => sum + brand.volume, 0)
  const withCounts = brands.map((brand) => ({
    ...brand,
    count: volumeSum > 0 ? count * (brand.volume / volumeSum) : 0,
  }))

  return {
    gross: volume,
    brandVolumeSum: volumeSum,
    count,
    useGrossForPercent: false,
    fees: scaledFees(statement, ratio),
    interchange: interchangeAmount === null ? null : money(interchangeAmount * ratio),
    brands: withCounts,
  }
}

function splitToTotal(total: number, weights: number[]): number[] {
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0)
  if (weights.length === 0) return []
  if (weightSum <= 0) {
    return weights.map((_, index) => (index === 0 ? total : 0))
  }
  const raw = weights.map((weight) => money(total * weight / weightSum))
  raw[0] = money((raw[0] ?? 0) + (total - raw.reduce((sum, value) => sum + value, 0)))
  return raw
}

function scaledFees(statement: StatementSnapshot, ratio: number): number | null {
  const detailed = (statement.entry_mode ?? 'detailed') === 'detailed'
  if (detailed) {
    const monthly = numberOrNull(statement.monthly_fees) ?? 0
    const variable = (numberOrNull(statement.discount_fees) ?? 0)
      + (numberOrNull(statement.per_item_fees) ?? 0)
      + (numberOrNull(statement.other_fees) ?? 0)
    return money(variable * ratio + monthly)
  }
  const total = numberOrNull(statement.total_fees)
  if (total === null) return null
  const monthly = numberOrNull(statement.fixed_monthly_fees) ?? 0
  return money(Math.max(0, total - monthly) * ratio + monthly)
}

function baseFees(statement: StatementSnapshot): number | null {
  if ((statement.entry_mode ?? 'detailed') !== 'quick') {
    return money(
      (numberOrNull(statement.discount_fees) ?? 0)
      + (numberOrNull(statement.per_item_fees) ?? 0)
      + (numberOrNull(statement.monthly_fees) ?? 0)
      + (numberOrNull(statement.other_fees) ?? 0),
    )
  }
  return numberOrNull(statement.total_fees)
}

function baseCount(statement: StatementSnapshot): number {
  if ((statement.entry_mode ?? 'detailed') === 'quick') {
    return BRANDS.reduce((sum, brand) => sum + (numberOrNull(statement[brand.count]) ?? 0), 0)
  }
  return numberOrNull(statement.transaction_count) ?? 0
}

function percentOf(amount: number, percent: number): number {
  return amount * percent / 100
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`
}

function dollars(value: number): string {
  return `$${value.toFixed(2)}`
}
